import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { LoginRequest, ApiError } from '../types';
import { useI18n } from '../hooks/useI18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  CheckCircle,
  AlertCircle,
  Leaf,
  BarChart3,
  Shield,
  Smartphone
} from 'lucide-react';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, isRTL } = useI18n();

  // Check for success message from password reset
  useEffect(() => {
    const resetSuccess = searchParams.get('resetSuccess');
    if (resetSuccess === 'true') {
      setSuccess(t('login.passwordResetSuccess'));
    }
  }, [searchParams, t]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await authAPI.login(formData.email, formData.password);

      if (response.token) {
        login(response.token);

        // Redirect to app route which will handle authentication-based routing
        navigate('/app');
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      setError(apiError.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="max-w-md text-center text-white px-8">
            <Link to="/" className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                <img src="/solvingers.svg" />
              </div>
              <h1 className="text-4xl font-bold">Facturelik</h1>
            </Link>

            <h2 className="text-3xl font-semibold mb-4">{t('login.welcomeBack')}</h2>
            <p className="text-lg text-white/90 mb-12">
              {t('login.accessInsights')}
            </p>

            <div className="space-y-8">
              <div className={`flex items-start space-x-4`}>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div className={"text-left"}>
                  <h3 className="text-lg font-semibold mb-2">{t('login.advancedAnalytics')}</h3>
                  <p className="text-white/80">{t('login.analyticsDescription')}</p>
                </div>
              </div>

              <div className={`flex items-start space-x-4`}>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div className={"text-left"}>
                  <h3 className="text-lg font-semibold mb-2">{t('login.securePlatform')}</h3>
                  <p className="text-white/80">{t('login.secureDescription')}</p>
                </div>
              </div>

              <div className={`flex items-start space-x-4`}>
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div className={"text-left"}>
                  <h3 className="text-lg font-semibold mb-2">{t('login.mobileReady')}</h3>
                  <p className="text-white/80">{t('login.mobileDescription')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>

          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">Facturelik</h1>
          </Link>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('login.title')}</h2>
            <p className="text-secondary-600">{t('login.subtitle')}</p>
          </div>

          {success && (
            <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
              <p className="text-success-800">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <p className="text-error-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="form-label">
                {t('login.email')}
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                  <Mail className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('login.email')}
                  className={`form-input ${isRTL ? 'pr-10' : 'pl-10'}`}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                {t('login.password')}
              </label>
              <div className="relative">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                  <Lock className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('login.password')}
                  className={`form-input ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}`}
                  required
                />
                <button
                  type="button"
                  className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-secondary-400 hover:text-secondary-600" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-secondary-700`}>{t('login.rememberMe')}</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <div className={`flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                  <div className="w-5 h-5 spinner"></div>
                  <span>{t('login.signingIn')}</span>
                </div>
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-secondary-600">
              {t('login.noAccount')}{' '}
              <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-semibold">
                {t('login.createAccount')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
